import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../lib/firebase";
import { StorageUploadResult } from "../types";

const BUCKET_NAME = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "mlc-proposal-hq-storage";

/**
 * Normalizes a path, stripping leading gs://<bucket>/ if present
 */
export function extractStoragePath(pathOrGs: string): string {
  if (pathOrGs.startsWith("gs://")) {
    return pathOrGs.replace(/^gs:\/\/[^/]+\//, "");
  }
  return pathOrGs.startsWith("/") ? pathOrGs.slice(1) : pathOrGs;
}

/**
 * Uploads an RFP source document or attachment for an opportunity
 */
export async function uploadRfpDocument(
  opportunityId: string,
  file: File,
): Promise<StorageUploadResult> {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const uniqueName = `${Date.now()}_${sanitizedFileName}`;
  const storagePath = `rfps/${opportunityId}/${uniqueName}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || "application/octet-stream",
    customMetadata: {
      originalName: file.name,
      opportunityId,
      uploadedAt: new Date().toISOString(),
    },
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    downloadUrl,
    storagePath: snapshot.metadata.fullPath,
    fullGsPath: `gs://${BUCKET_NAME}/${snapshot.metadata.fullPath}`,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type || "application/octet-stream",
  };
}

/**
 * Uploads a compliance/vault document (certificates, insurance, references)
 */
export async function uploadVaultDocument(
  category: string,
  file: File,
): Promise<StorageUploadResult> {
  const sanitizedCategory = category.toLowerCase().replace(/[^a-z0-9_-]/g, "_");
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `vault/${sanitizedCategory}/${Date.now()}_${sanitizedFileName}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || "application/octet-stream",
    customMetadata: {
      originalName: file.name,
      category: sanitizedCategory,
      uploadedAt: new Date().toISOString(),
    },
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    downloadUrl,
    storagePath: snapshot.metadata.fullPath,
    fullGsPath: `gs://${BUCKET_NAME}/${snapshot.metadata.fullPath}`,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type || "application/octet-stream",
  };
}

/**
 * Retrieves a secure HTTPS download URL for a stored document
 */
export async function getDocumentDownloadUrl(pathOrGs: string): Promise<string> {
  const path = extractStoragePath(pathOrGs);
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
}

/**
 * Deletes a document from the isolated storage bucket
 */
export async function deleteStorageDocument(pathOrGs: string): Promise<void> {
  const path = extractStoragePath(pathOrGs);
  const storageRef = ref(storage, path);
  return deleteObject(storageRef);
}
